import { isNotFound, MedplumClient, OperationOutcomeError } from "@medplum/core";
import { Provenance } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import type {
  CreateLaborEncounterAmendCompletionProvenanceParams,
  CreateLaborEncounterAmendCompletionProvenanceResult,
} from "@/encounters/components/EncounterLabor/types/labor-amend-completion-provenance";
import { fetchEncounterFlowsheetQuestionnaireResponsesById } from "@/encounters/components/EncounterLabor/utils/fetch-encounter-flowsheet-questionnaire-responses";
import {
  LABOR_AMEND_COMPLETION_PROVENANCE_CREATE_FAILED_MESSAGE,
  LaborAmendCompletionProvenanceFailureReason,
} from "@/encounters/constants/labor-amend-completion-provenance-failure-reason";
import { LaborEncounterProvenanceActivityCode } from "@/encounters/constants/labor-encounter-provenance";
import type { LaborAmendStageMembershipChangeRecord } from "@/encounters/types/labor-amend-provenance";
import {
  getLaborAmendStageMembershipChangesFromProvenance,
  setLaborAmendStageMembershipChangesOnProvenance,
} from "@/encounters/utils/labor-amend-provenance-stage-moves";
import {
  buildLaborEncounterAmendCompletionProvenanceIfNoneExistQuery,
  buildLaborEncounterAmendCompletionProvenanceResourceId,
  buildLaborEncounterEventProvenance,
  buildLaborEncounterEventProvenanceTargets,
  filterFlowsheetQuestionnaireResponsesByEncounterParentVersionId,
  findLaborEncounterAmendCompletionProvenanceInBundle,
  getEncounterAmendmentReasonFromFormValues,
  isLaborEncounterAmendCompletionProvenanceForEncounterVersion,
} from "@/encounters/utils/labor-encounter-event-provenance";
import { mergeLaborAmendProvenanceTargets } from "@/encounters/utils/merge-labor-amend-provenance-targets";
import { resolveLaborAmendProvenancePractitionerReference } from "@/encounters/utils/resolve-labor-amend-provenance-practitioner";
import { joinReferenceString } from "@/utils/medplum";

function isMedplumNotFoundError(error: unknown): error is OperationOutcomeError {
  return error instanceof OperationOutcomeError && isNotFound(error.outcome);
}

/**
 * Returns whether a Provenance was newly created in this request, as opposed to an existing
 * record returned by conditional create after a concurrent write.
 */
function wasProvenanceJustCreated(provenance: Provenance, createStartedAtMs: number): boolean {
  if (!provenance.meta?.lastUpdated) {
    return false;
  }

  const lastUpdatedMs = new Date(provenance.meta.lastUpdated).getTime();
  return lastUpdatedMs >= createStartedAtMs - 2000;
}

/**
 * Finds an existing amend-completion Provenance by deterministic id, then by QuestionnaireResponse target.
 */
export async function findLaborEncounterAmendCompletionProvenance(
  medplum: MedplumClient,
  encounterQuestionnaireResponseId: string,
  encounterQuestionnaireResponseVersionId: string,
): Promise<Provenance | undefined> {
  const resourceId = buildLaborEncounterAmendCompletionProvenanceResourceId(
    encounterQuestionnaireResponseId,
    encounterQuestionnaireResponseVersionId,
  );

  try {
    const provenance = await medplum.readResource("Provenance", resourceId);
    if (
      isLaborEncounterAmendCompletionProvenanceForEncounterVersion(
        provenance,
        encounterQuestionnaireResponseId,
        encounterQuestionnaireResponseVersionId,
      )
    ) {
      return provenance;
    }
  } catch (error) {
    if (!isMedplumNotFoundError(error)) {
      throw error;
    }
  }

  const candidates = await medplum.searchResources("Provenance", {
    target: joinReferenceString("QuestionnaireResponse", encounterQuestionnaireResponseId),
  });

  return findLaborEncounterAmendCompletionProvenanceInBundle(
    candidates,
    encounterQuestionnaireResponseId,
    encounterQuestionnaireResponseVersionId,
  );
}

interface BackfillExistingProvenanceStageMembershipParams {
  medplum: MedplumClient;
  existingProvenance: Provenance;
  stageMembershipChanges: LaborAmendStageMembershipChangeRecord[];
  encounterId: string;
  encounterQuestionnaireResponseId: string;
  encounterQuestionnaireResponseVersionId: string;
}

/**
 * Backfills stage membership changes onto an existing completion Provenance when absent.
 * Returns undefined when no update is needed.
 */
async function backfillExistingProvenanceStageMembership({
  medplum,
  existingProvenance,
  stageMembershipChanges,
  encounterId,
  encounterQuestionnaireResponseId,
  encounterQuestionnaireResponseVersionId,
}: BackfillExistingProvenanceStageMembershipParams): Promise<
  CreateLaborEncounterAmendCompletionProvenanceResult | undefined
> {
  const existingStageMembershipChanges =
    getLaborAmendStageMembershipChangesFromProvenance(existingProvenance);
  if (existingStageMembershipChanges.length > 0) {
    return undefined;
  }

  try {
    const provenance = await medplum.updateResource(
      setLaborAmendStageMembershipChangesOnProvenance(existingProvenance, stageMembershipChanges),
    );
    return { ok: true, provenance, created: false };
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      extra: {
        encounterId,
        encounterQuestionnaireResponseId,
        encounterQuestionnaireResponseVersionId,
      },
    });
    return {
      ok: false,
      reason: LaborAmendCompletionProvenanceFailureReason.CreateFailed,
      details: LABOR_AMEND_COMPLETION_PROVENANCE_CREATE_FAILED_MESSAGE,
    };
  }
}

/**
 * Creates (or reuses) a Provenance recording a completed labor encounter amendment.
 * Idempotent per encounter QuestionnaireResponse `meta.versionId` via conditional create on `_id`.
 */
export async function createLaborEncounterAmendCompletionProvenance({
  medplum,
  clientId,
  encounterId,
  encounterQuestionnaireResponse,
  practitionerReference,
  formValues,
  amendProvenanceSnapshot,
}: CreateLaborEncounterAmendCompletionProvenanceParams): Promise<CreateLaborEncounterAmendCompletionProvenanceResult> {
  const encounterQuestionnaireResponseVersionId = encounterQuestionnaireResponse.meta?.versionId;
  if (!encounterQuestionnaireResponseVersionId) {
    return { ok: false, reason: LaborAmendCompletionProvenanceFailureReason.MissingVersionId };
  }

  if (!encounterQuestionnaireResponse.id) {
    return {
      ok: false,
      reason: LaborAmendCompletionProvenanceFailureReason.MissingQuestionnaireResponseId,
    };
  }

  let existingProvenance: Provenance | undefined;
  try {
    existingProvenance = await findLaborEncounterAmendCompletionProvenance(
      medplum,
      encounterQuestionnaireResponse.id,
      encounterQuestionnaireResponseVersionId,
    );
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      extra: {
        encounterId,
        encounterQuestionnaireResponseId: encounterQuestionnaireResponse.id,
        encounterQuestionnaireResponseVersionId,
      },
    });
    return {
      ok: false,
      reason: LaborAmendCompletionProvenanceFailureReason.CreateFailed,
      details: LABOR_AMEND_COMPLETION_PROVENANCE_CREATE_FAILED_MESSAGE,
    };
  }

  if (existingProvenance) {
    const stageMembershipChanges = amendProvenanceSnapshot?.stageMembershipChanges;
    if (stageMembershipChanges?.length) {
      const backfillResult = await backfillExistingProvenanceStageMembership({
        medplum,
        existingProvenance,
        stageMembershipChanges,
        encounterId,
        encounterQuestionnaireResponseId: encounterQuestionnaireResponse.id,
        encounterQuestionnaireResponseVersionId,
      });
      if (backfillResult) {
        return backfillResult;
      }
    }

    return { ok: true, provenance: existingProvenance, created: false };
  }

  const flowsheetQuestionnaireResponsesById =
    await fetchEncounterFlowsheetQuestionnaireResponsesById(medplum, clientId, encounterId);

  const stampedFlowsheetQuestionnaireResponses =
    filterFlowsheetQuestionnaireResponsesByEncounterParentVersionId(
      (flowsheetQuestionnaireResponsesById ?? new Map()).values(),
      encounterQuestionnaireResponseVersionId,
      encounterQuestionnaireResponse.id,
    );

  const resolvedPractitionerReference = await resolveLaborAmendProvenancePractitionerReference({
    medplum,
    clientId,
    encounterId,
    encounterQuestionnaireResponseVersionId,
    practitionerReference,
    stampedFlowsheetQuestionnaireResponses,
    amendProvenanceSnapshot,
  });

  if (!resolvedPractitionerReference) {
    return { ok: false, reason: LaborAmendCompletionProvenanceFailureReason.MissingPractitioner };
  }

  const provenanceResourceId = buildLaborEncounterAmendCompletionProvenanceResourceId(
    encounterQuestionnaireResponse.id,
    encounterQuestionnaireResponseVersionId,
  );

  const baseTargets = buildLaborEncounterEventProvenanceTargets(
    encounterId,
    encounterQuestionnaireResponse,
    stampedFlowsheetQuestionnaireResponses,
  );

  const targets = amendProvenanceSnapshot?.targets.length
    ? mergeLaborAmendProvenanceTargets(baseTargets, amendProvenanceSnapshot.targets)
    : baseTargets;

  let provenanceResource: Provenance = {
    ...buildLaborEncounterEventProvenance({
      activityCode: LaborEncounterProvenanceActivityCode.AmendCompletion,
      encounterQuestionnaireResponseVersionId,
      targets,
      practitionerReference: resolvedPractitionerReference,
      amendmentReason: getEncounterAmendmentReasonFromFormValues(formValues),
    }),
    id: provenanceResourceId,
  };

  if (amendProvenanceSnapshot?.stageMembershipChanges.length) {
    provenanceResource = setLaborAmendStageMembershipChangesOnProvenance(
      provenanceResource,
      amendProvenanceSnapshot.stageMembershipChanges,
    );
  }

  try {
    const createStartedAtMs = Date.now();
    const provenance = await medplum.createResourceIfNoneExist(
      provenanceResource,
      buildLaborEncounterAmendCompletionProvenanceIfNoneExistQuery(
        encounterQuestionnaireResponse.id,
        encounterQuestionnaireResponseVersionId,
      ),
    );
    return {
      ok: true,
      provenance,
      created: wasProvenanceJustCreated(provenance, createStartedAtMs),
    };
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      extra: {
        encounterId,
        encounterQuestionnaireResponseId: encounterQuestionnaireResponse.id,
        encounterQuestionnaireResponseVersionId,
      },
    });
    return {
      ok: false,
      reason: LaborAmendCompletionProvenanceFailureReason.CreateFailed,
      details: LABOR_AMEND_COMPLETION_PROVENANCE_CREATE_FAILED_MESSAGE,
    };
  }
}
